import type { PaginatedResponse, PaginationOptions } from '@/types/api.types.ts'
import {
  HTTP_GET_FILTERS,
  HTTP_GET_FILTER_DEFAULT_VALUES,
} from '@/constants/apiConstants.ts'

const secureFetch = async <T>(url: URL, resourceLabel: string): Promise<T> => {
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(
      `Failed to fetch ${resourceLabel}: ${response.statusText} ${response.status}`,
    )
  }

  return response.json()
}

const doPaginatedFetch = async <T>(url: URL, resourceLabel: string) => {
  return secureFetch<PaginatedResponse<T>>(url, resourceLabel)
}

export const resourceWithPagination =
  <TItem>(buildPath: () => string, resourceLabel: string) =>
  async ({
    offset = HTTP_GET_FILTER_DEFAULT_VALUES[HTTP_GET_FILTERS.OFFSET],
    limit = HTTP_GET_FILTER_DEFAULT_VALUES[HTTP_GET_FILTERS.LIMIT],
  }: PaginationOptions = {}) => {
    const url = new URL(buildPath(), window.location.origin)

    url.searchParams.set(HTTP_GET_FILTERS.OFFSET, offset.toString())
    url.searchParams.set(HTTP_GET_FILTERS.LIMIT, limit.toString())

    return doPaginatedFetch<TItem>(url, resourceLabel)
  }

export const resourceWithSecureFetch =
  <TItem, TId>(buildPath: (id: TId) => string, resourceLabel: string) =>
  async (id: TId) => {
    const url = new URL(buildPath(id), window.location.origin)

    return secureFetch<TItem>(url, resourceLabel)
  }

export const subresourceWithPagination =
  <TItem, TId>(buildPath: (id: TId) => string, resourceLabel: string) =>
  async (
    id: TId,
    {
      offset = HTTP_GET_FILTER_DEFAULT_VALUES[HTTP_GET_FILTERS.OFFSET],
      limit = HTTP_GET_FILTER_DEFAULT_VALUES[HTTP_GET_FILTERS.LIMIT],
    }: PaginationOptions = {},
  ) => {
    const url = new URL(buildPath(id), window.location.origin)

    url.searchParams.set(HTTP_GET_FILTERS.OFFSET, offset.toString())
    url.searchParams.set(HTTP_GET_FILTERS.LIMIT, limit.toString())

    return doPaginatedFetch<TItem>(url, resourceLabel)
  }
